import React, { createContext, useContext, useState, useEffect, useMemo, useCallback, useRef } from 'react';
import useConversations from '../hooks/useConversations.js';
import { useAudio } from '../hooks/useAudio.js';
import { socketService } from '../services/websocketService.js';
import { useToast } from '../hooks/use-toast.js';

const ChatContext = createContext({});

const WS_URL = process.env.REACT_APP_WS_URL;

export const ChatProvider = ({ children }) => {
    const {
        conversations,
        currentConversation,
        messages,
        createNewConversation,
        switchConversation,
        addMessage,
        clearConversationMessages,
    } = useConversations();
    const [isLoading, setIsLoading] = useState(false);
    const [recording, setRecording] = useState(false);
    const [speaking, setSpeaking] = useState(false);
    const { playAudio } = useAudio();
    const [processingVoiceId, setProcessingVoiceId] = useState(null);
    const [tempMessageId, setTempMessageId] = useState(null);
    const { toast } = useToast();
    const [conversationCreating, setConversationCreating] = useState(false);
    const socketConnectedRef = useRef(false);

    const clearMessages = useCallback(() => {
        clearConversationMessages();
    }, [clearConversationMessages]);

    // Create a conversation if none exists
    useEffect(() => {
        const createConversationIfNeeded = async () => {
            // Wait a bit for conversations to load, then check if we have one
            if (conversationCreating) return;
            
            if (!currentConversation?.id) {
                setConversationCreating(true);
                try {
                    await createNewConversation();
                    toast({
                        title: "Conversation Created",
                        description: "Starting a new conversation",
                        duration: 2000,
                    });
                } catch (error) {
                    console.error('Failed to create conversation:', error);
                    toast({
                        title: "Error",
                        description: "Failed to create conversation",
                        variant: "destructive",
                        duration: 3000,
                    });
                } finally {
                    setConversationCreating(false);
                }
            }
        };

        // Wait for initial load to complete before creating
        const timer = setTimeout(createConversationIfNeeded, 500);
        return () => clearTimeout(timer);
    }, [currentConversation?.id, createNewConversation, conversationCreating, toast]);

    useEffect(() => {
        if (!currentConversation?.id) return;
        
        // Prevent duplicate connections
        if (socketConnectedRef.current) {
            console.log('[ChatContext] Already connected, skipping...');
            return;
        }
        
        console.log('[ChatContext] Connecting to socket with conversation:', currentConversation.id);
        
        socketService.connect(`${WS_URL}/chat`, {
            auth: { token: localStorage.getItem('accessToken') || undefined },
            onConnect: () => {
                socketService.joinRoom(currentConversation.id);
                socketConnectedRef.current = true;
                toast({
                    title: "Connected",
                    description: "Successfully connected to chat",
                    duration: 2000,
                });
            },
            onMessage: (role, content, metadata) => {
                setIsLoading(false);
                setProcessingVoiceId(null);
                setTempMessageId(null);
                const formattedContent = Array.isArray(content) ? content.map(item => ({
                    type: item.type,
                    value: item.value,
                    language: item.language,
                    metadata: item.metadata,
                    ...item,
                    convoId: currentConversation.id
                })) : [];
                console.log('[ChatContext] Received message:', { role, content, metadata });
                addMessage(role, formattedContent, metadata);
                if (role === 'assistant') {
                    toast({
                        title: "Response Received",
                        description: "Your request has been processed successfully",
                        duration: 3000,
                    });
                }
            },
            onAudio: (url) => {
                setSpeaking(true);
                playAudio(url);
                setTimeout(() => setSpeaking(false), 3000);
            },
            onError: (err) => {
                setIsLoading(false);
                setRecording(false);
                setProcessingVoiceId(null);
                setTempMessageId(null);
                toast({
                    title: "Connection Error",
                    description: err || "Failed to connect to chat service",
                    variant: "destructive",
                    duration: 5000,
                });
            },
        });
        
        return () => {
            socketService.disconnect();
            socketConnectedRef.current = false;
        };
    }, [currentConversation?.id]);

    const sendMessage = useCallback((text) => {
        if (!text.trim() || !currentConversation?.id) return;
        addMessage('user', [{
            type: 'text',
            value: text,
            convoId: currentConversation.id
        }]);
        setIsLoading(true);
        socketService.sendMessage(text, currentConversation.id);
    }, [currentConversation?.id, addMessage]);

    const contextValue = useMemo(() => ({
        messages,
        conversations,
        currentConversation,
        isLoading,
        recording,
        speaking,
        sendMessage,
        createNewConversation,
        switchConversation,
        clearMessages,
    }), [
        messages,
        conversations,
        currentConversation,
        isLoading,
        recording,
        speaking,
        sendMessage,
        createNewConversation,
        switchConversation,
        clearMessages,
    ]);

    return (
        <ChatContext.Provider value={contextValue}>
            {children}
        </ChatContext.Provider>
    );
};

export const useChat = () => useContext(ChatContext); 